import { useApp } from "@/context/AppContext";
import { TRANSLATIONS, type TranslationKey } from "./translations";
import { type SupportedLocale } from "./locales";

// Basic static exchange rates against USD for demo purposes.
// In a real production app, these would be fetched periodically from an API.
const FX_RATES: Record<string, number> = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 150.5,
  CNY: 7.2,
};

export function useTranslation() {
  const { locale, setLocale, preferredCurrency } = useApp() as any;

  /**
   * Translates a key based on active locale, falling back to English.
   * Supports placeholder interpolation, e.g. t("orderbook.recentPrints", { count: 12 })
   */
  const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
    const activeLocale: SupportedLocale = locale || "en";
    const dict = TRANSLATIONS[activeLocale] || TRANSLATIONS.en;
    let text = (dict as any)[key] || (TRANSLATIONS.en as any)[key] || String(key);
    
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
      });
    }
    
    return text;
  };

  /** Localized currency formatter automatically converted to preferred fiat currency */
  const formatCurrency = (valUsd: number, overrideCurrency?: string): string => {
    const targetCurrency = overrideCurrency || preferredCurrency || "USD";
    const rate = FX_RATES[targetCurrency] || 1;
    const converted = valUsd * rate;
    
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: targetCurrency,
      }).format(converted);
    } catch {
      return `$${converted.toLocaleString()}`;
    }
  };

  /** Localized numerical formatter with configurable decimal places */
  const formatNumber = (val: number, decimals?: number): string => {
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(val);
    } catch {
      return val.toLocaleString();
    }
  };

  /** Localized date/time formatter */
  const formatDate = (date: Date | number, options?: Intl.DateTimeFormatOptions): string => {
    try {
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch {
      return new Date(date).toLocaleString();
    }
  };

  return {
    t,
    locale: locale as SupportedLocale,
    setLocale: setLocale as (l: SupportedLocale) => void,
    formatCurrency,
    formatNumber,
    formatDate,
  };
}

